import { Cell, createColumnHelper } from "@tanstack/react-table";
import merge from "lodash/merge";

import { FormatDate } from "@/components/Date";
import { LineItemUOMQuantity } from "@/features/job-tracking/components/LineItemUOMQuantity";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { Shipment, ShipmentLineItem } from "../../queries/tracking-data/shipments";
import { BinCell } from "../BinCell";
import { LineItemDescription } from "../LineItemDescription";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";

export interface ShipmentTableRow {
  rowId: string;
  shipment: Shipment;
  line: ShipmentLineItem;
}

// For shipment/output columns, set the rowspan of each cell on the on the first line to the total number of lines. Hide all subsequent rows for the same receipt
const shipmentColCellProps = (
  cell: Cell<ShipmentTableRow, unknown>,
  additionalProps?: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "rowSpan">,
) => {
  const { shipment, line } = cell.row.original;
  const firstLineId = shipment.jobLines[0]!.id;
  const isFirstLine = line.id === firstLineId;
  return isFirstLine ?
      { ...additionalProps, rowSpan: shipment.jobLines.length }
    : { ...additionalProps, className: "d-none " + (additionalProps?.className ?? "") };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<ShipmentTableRow>();
const shipmentAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: shipmentColCellProps } }, column));
const lineItemAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ enableSorting: false }, column));

const shipmentColumns = [
  shipmentAccessorCol("shipment.number", {
    header: "Shipment #",
    cell: ({ getValue, row }) => <SosEntityLink type="shipment" id={row.original.shipment.id} label={getValue()} />,
  }),
  shipmentAccessorCol("shipment.date", {
    header: "Date",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  shipmentAccessorCol((x) => x.shipment.shippingMethod?.name ?? null, {
    id: "shipment.shippingMethod.name",
    header: "Shipping Method",
  }),
  shipmentAccessorCol("shipment.trackingNumber", {
    header: "Tracking #",
    cell: ({ row, getValue }) => {
      const trackingNumber = getValue();
      const trackingLink = row.original.shipment.trackingLink;
      if (!trackingLink) return trackingNumber;
      return (
        <a href={trackingLink} target="_blank" rel="noreferrer">
          {trackingNumber}
        </a>
      );
    },
  }),
];

const lineItemColumns = [
  lineItemAccessorCol("line.lineNumber", {
    header: "Line #",
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: { className: "border-start-double" },
    },
  }),
  lineItemAccessorCol("line.item.name", {
    header: "Item",
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.line.item.id}
            label={row.original.line.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  lineItemAccessorCol("line.item.type", { header: "Type" }),
  lineItemAccessorCol("line.item.description", {
    header: "Description",
    cell: ({ row }) => <LineItemDescription line={row.original.line} />,
  }),
  lineItemAccessorCol("line.uomQuantity", {
    header: "Quantity",
    cell: ({ row }) => <LineItemUOMQuantity line={row.original.line} />,
  }),
  lineItemAccessorCol("line.bin.number", {
    header: "From Bin",
    cell: ({ row }) => <BinCell bin={row.original.line.bin} />,
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const shipmentTableColumns = [
  columnHelper.group({ header: "Shipments", columns: shipmentColumns }),
  columnHelper.group({
    header: "Line Items",
    columns: lineItemColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
];
